import React, { useContext, useRef, useState } from 'react';
import { NotificationManager } from 'src/lib/notification-manager';

interface NotificationContextValue {
    permission: NotificationPermission;
    requestNotificationPermission: () => Promise<void>;
    showNotification: (title: string, description: string, persist?: boolean) => Promise<void>;
    closeNotifications: () => Promise<void>;
}

const NotificationContext = React.createContext<NotificationContextValue>({
    permission: 'default',
    requestNotificationPermission: async () => {},
    showNotification: async () => {},
    closeNotifications: async () => {},
});

export const NotificationConsumer = NotificationContext.Consumer;

export const useNotification = () => useContext(NotificationContext);

export const NotificationProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
    const [permission, setPermission] = useState<NotificationPermission>('default');
    const notificationManagerRef = useRef(new NotificationManager());

    async function requestNotificationPermission() {
        if (NotificationManager.isSupported()) {
            try {
                setPermission(await NotificationManager.requestPermission());
            } catch {
                setPermission('denied');
            }
        }
    }

    async function showNotification(title: string, description: string, persist: boolean = false) {
        notificationManagerRef.current.showNotification(title, description, persist);
    }

    async function closeNotifications() {
        notificationManagerRef.current.closeNotifications();
    }

    return (
        <NotificationContext.Provider
            value={{
                permission,
                requestNotificationPermission,
                showNotification,
                closeNotifications,
            }}
        >
            {children}
        </NotificationContext.Provider>
    );
};
